import { StandardTrialNoticeRoot } from '#~/account/components/settings/billing/standard-trial-notice-root.jsx';
import { BillingPlanLinkRoot } from '#~/account/components/settings/billing/billing-plan-link-root.jsx';
import { useSettings } from '#~/account/components/hooks.js';
import { Panel } from '#~/account/components/settings/types.js';
import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import type { MultiBridgeRequestDialogProps } from '#~/agent/components/bridge/multi-bridge/dialog.js';
import { MultiBridgeRequestDialog } from '#~/agent/components/bridge/multi-bridge/dialog.js';
import { useBridgeDiscoveryLinkingStore } from '#~/agent/components/bridge/screens/search-devices.js';
import { useAgents } from '#~/agent/hooks.js';
import { Provider as AgentProvider } from '#~/agent/provider/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES, useAnalytics } from '#~/analytics/index.js';
// Direct (non-barrel) import on purpose — see the ENG-4306 NOTE in analytics/domains/index.ts.
import { createBootLifecycleTracker } from '#~/analytics/domains/boot-lifecycle.js';
import { setActiveStudio, clearActiveStudio } from '#~/analytics/active-studio.js';
import { subscribePlanPaymentReturns } from '#~/api/billing/payment-return.js';
import { usePlanPurchases } from '#~/api/billing/use-plan-purchase.js';
import { useBillingLimitMonitor } from '#~/api/billing/limits.js';
import { verifyBridgeDiscoveryLinkingAuthRequest } from '#~/api/bridge-discovery/registry.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { InitialBillingGate } from './initial-billing-gate.js';
import { config } from '#~/config/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import { INITIAL_BOOT_OBSERVATION } from '#~/depot/types.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { evaluateBridgeLink, PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { NotFoundError } from '#~/error/index.js';
import { hide, post } from '#~/notifications/utils.js';
import { usePrefState } from '#~/preferences/hooks.js';
import { AddVideoModal } from '#~/room/components/video/add.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import { Provider as RoomProvider } from '#~/room/provider/index.js';
import { useUser } from '#~/user/hooks.js';
import { useDialog } from '@studio/design-system/dialog';
import type { Notification } from '@studio/design-system/notification';
import { BridgeLinkingRequestActions } from '@studio/design-system/notification';
import { usePrompt } from '@studio/design-system/prompt';
import type { ChildrenOnly } from '@studio/design-system/props';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { parseQuery } from '@studio/models/net';
import type { Room as M_Room } from '@studio/models/room';
import type { User } from '@studio/models/user';
import { useLatestFn, useLazyRef, useMount } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { toBoolean } from '@tryiris-ai/js-types';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { ReactElement } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Paths } from '../constants.js';
import { OnboardPermissionsModal } from '../electron/permissions/permissions.js';
import { AutoLinkLoadingScreen } from '../verify-auto-link/v2/auto-link-loading.js';
import { getBootId } from '#~/logger/index.js';
import {
  bootAccessFrom,
  bootRoomFactsFrom,
  computeRoomBootReadiness,
  EMPTY_STUDIO_GRACE_MS,
  roomCanControlCacheKey,
  shouldArmEmptyStudioGrace,
  type RoomBootReadiness
} from './boot-readiness.js';
import { Page } from './page.jsx';
import { Placeholder } from './placeholder.js';
import type { Props } from './types.js';

let prepApprovalForUnlink = false;

// Per y4fp5-W1 (seamless-load): use the chrome-identical loading shell
// while room data hydrates, so when BootOverlay fades the chrome
// underneath is byte-identical (no flash to a no-text spinner).
const SEAMLESS_LOAD_STATE = { status: 'idle' as const };

/** @internal Room inner component. */
const Inner = ({ roomId, ...props }: Props & { roomId: M_Room['id'] }): ReactElement => {
  return <Page roomId={roomId} {...props} />;
};

const useRoomBootReadiness = (roomId: M_Room['id']): RoomBootReadiness => {
  const hasRoomData = useDepot((µ) => !!µ.rooms[roomId]);
  const statuses = useAgents((µ) => µ.map((a) => a.status), shallow);
  const { agentId: machineAgentId, resolution: machineAgentResolution } = useGetMachineAgentId();

  // A bridge-less Studio settles at zero agents and no machine identity. Keep
  // linked Studios behind the overlay while rapid-refresh enumeration catches up.
  const isBridgeless = shouldArmEmptyStudioGrace({
    hasRoomData,
    statuses,
    machineAgentId,
    machineAgentResolution
  });
  const [bridgelessGraceElapsed, setBridgelessGraceElapsed] = useState(false);

  useEffect(() => {
    if (!isBridgeless) {
      setBridgelessGraceElapsed(false);

      return;
    }

    const timer = setTimeout(() => setBridgelessGraceElapsed(true), EMPTY_STUDIO_GRACE_MS);

    return () => clearTimeout(timer);
  }, [isBridgeless]);

  return computeRoomBootReadiness({ hasRoomData, statuses, bridgelessGraceElapsed });
};

const RoomBootReadinessPublisher = ({
  ready,
  roomId
}: {
  readonly ready: boolean;
  readonly roomId: M_Room['id'];
}): null => {
  const [produce, record, room, hasRegistryLoaded, accountId] = useDepot(
    (µ) =>
      [
        µ.produce,
        µ.roomRecords?.[roomId],
        µ.rooms?.[roomId],
        µ.synchronizer?.hasRegistryLoaded ?? false,
        µ.user?.id ?? null
      ] as const,
    shallow
  );

  // Mirror page.tsx: the record's canControl wins, the last visit's cached value
  // fills in, and the page renders (no Loader) whenever either is known.
  const [cachedCanControl] = usePrefState<boolean | undefined>(
    roomCanControlCacheKey(roomId),
    undefined
  );
  const renderedCanControl = record?.permissions?.canControl ?? cachedCanControl;

  // Room owns visible-ready and boot facts. Overlay owns outcome=ready.
  useLayoutEffect(() => {
    produce((draft) => {
      draft.boot.isRootReady = ready;
      const previous = draft.boot.observation?.outcome;
      draft.boot.observation = {
        ...(draft.boot.observation ?? INITIAL_BOOT_OBSERVATION),
        outcome:
          previous === 'ready' || previous === 'failed' || previous === 'awaiting_user'
            ? previous
            : 'pending',
        roomId,
        access: bootAccessFrom({ role: record?.permissions?.role, renderedCanControl }),
        registry: hasRegistryLoaded ? 'loaded' : 'unknown',
        roomFacts: bootRoomFactsFrom({
          recordPresent: Boolean(record),
          rendered: renderedCanControl !== undefined,
          ready,
          roomPresent: Boolean(room)
        }),
        accountId: accountId ?? null,
        bootId: getBootId()
      };
    });
  }, [produce, ready, roomId, record, room, hasRegistryLoaded, accountId, renderedCanControl]);

  return null;
};

/** @internal Common room component. */
const Common = ({ roomId }: { roomId: M_Room['id'] }) => {
  return (
    <>
      {/* Add single instance modals. */}
      <AddVideoModal roomId={roomId} />
      <BillingPlanLinkRoot key={`billing-link-${roomId}`} roomId={roomId} />
      <StandardTrialNoticeRoot key={roomId} roomId={roomId} />
    </>
  );
};

/** @internal Room guard. */
const Guard = ({
  children,
  roomId
}: ChildrenOnly & {
  roomId: M_Room['id'];
  user: { name: string; avatar: string | T_Maybe<string>; email: string; userId: User['id'] };
}): ReactElement => {
  const readiness = useRoomBootReadiness(roomId);

  const { track } = useAnalytics();
  const { getAccessTokenSilently } = useAuth0();
  const { setOwnsBridge } = useBridgeDiscoveryLinkingStore();
  const { enabled: bridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE);
  const { enabled: multiBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.MULTIBRIDGE_FEATURE);
  const hasExistingBridge = useAgents((agents) => agents.some((agent) => !agent.embedded));
  const bridgeLinkDecision = evaluateBridgeLink(
    config.vendor,
    hasExistingBridge,
    bridgeEnabled,
    multiBridgeEnabled
  );
  const bridgeLinkContextRef = useRef({
    decision: bridgeLinkDecision,
    hasExistingBridge,
    vendor: config.vendor
  });

  useEffect(() => {
    bridgeLinkContextRef.current = {
      decision: bridgeLinkDecision,
      hasExistingBridge,
      vendor: config.vendor
    };
  }, [bridgeLinkDecision, hasExistingBridge]);

  // TODO: TAM clean: would prefer to create a watchdog for this logic
  const { open: openDialog, close: closeDialog } =
    useDialog<MultiBridgeRequestDialogProps>(MultiBridgeRequestDialog);
  const prompt = usePrompt();

  // Create ref to track latest machineAgentId
  const machineAgentIdRef = useRef<Agent['id'] | undefined>(undefined);
  const { agentId: machineAgentId } = useGetMachineAgentId();

  // Keep ref updated with latest value
  useEffect(() => {
    machineAgentIdRef.current = machineAgentId;
  }, [machineAgentId]);

  const navigate = useNavigate();

  const postLinkingRequestApproval = () => {
    navigate('/' + Paths.agent.linking);
  };

  const handleConfirmClose = useLatestFn(async () => {
    closeDialog();

    if (!!machineAgentIdRef.current) {
      if (prepApprovalForUnlink) {
        // The machine agent is linked to a bridge, but the user is the owner of the bridge.
        // Automatically unlink the bridge and link to the new one.
        await ElectronAPI.mDNS.approveLinkRequest();
        postLinkingRequestApproval();

        return;
      }

      // The machine agent is already linked to a bridge.
      prompt.confirm({
        title: 'Unlink Bridge',
        message: [
          'This Bridge is currently linked to another Studio.',
          'By continuing, it will be unlinked from the existing Studio and linked with this one. This change cannot be undone.'
        ].join('\n'),
        okay: {
          variant: 'danger',
          label: 'Unlink Bridge'
        },
        cancel: {
          label: 'Cancel'
        },
        onConfirm: async (answer) => {
          if (!answer) {
            return;
          }

          track(EVENT_NAMES.OVERRIDES_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
            machineAgentId: machineAgentIdRef.current
          });

          await ElectronAPI.mDNS.approveLinkRequest();
          postLinkingRequestApproval();
        }
      });
    } else {
      await ElectronAPI.mDNS.approveLinkRequest();
      postLinkingRequestApproval();
    }
  });

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    let notificationId: Notification['id'];

    const handleClose = async () => {
      closeDialog();
      ElectronAPI.mDNS.denyLinkRequest();
    };

    const handleLinkRequest = async (event: CustomEvent) => {
      // Incoming link requests are an operation boundary, not just a visible
      // affordance. The base flag gates every link; additional links also
      // require Hive plus its independently available MultiBridge flag.
      const { decision, hasExistingBridge: hasBridge, vendor } = bridgeLinkContextRef.current;
      if (!decision.allowed) {
        console.warn('[BridgeLink] Link request denied', {
          reason: decision.reason,
          vendor,
          hasExistingBridge: hasBridge
        });
        ElectronAPI.mDNS.denyLinkRequest();
        return;
      }

      const data = event.detail;

      const request = {
        studioName: data.studio_name,
        requesterEmail: data.requester_email,
        isPreApprovedForUnlink: data.is_pre_approved_for_unlink,
        bridgeLinkingRequestId: data.bridge_linking_request
      };

      // 2) Verify the linking request coming in:
      const isSameUserRequesting = await verifyBridgeDiscoveryLinkingAuthRequest({
        token: await getAccessTokenSilently(), // token of the requestee
        requestId: request.bridgeLinkingRequestId
      });

      setOwnsBridge(isSameUserRequesting);
      prepApprovalForUnlink = request.isPreApprovedForUnlink && isSameUserRequesting;

      if (isSameUserRequesting) {
        handleConfirmClose();
        return;
      }

      const postLinkRequestNotification = () => {
        notificationId = post(
          {
            title: 'Bridge Linking Request',
            actionable: true,
            message: (
              <BridgeLinkingRequestActions
                userEmail={request.requesterEmail}
                onClose={() => {
                  hide(notificationId);
                }}
                onConfirm={() => {
                  hide(notificationId);
                  openDialog({
                    unlinkPreapproval: request.isPreApprovedForUnlink,
                    studioName: request.studioName,
                    userEmail: request.requesterEmail,
                    onConfirm: () => {
                      handleConfirmClose();
                    },
                    onDismiss: () => {
                      handleClose();
                    }
                  });
                }}
              />
            )
          },
          {
            autoClose: 60_000
          }
        );
      };

      postLinkRequestNotification();
    };

    const handleLinkRequestCancel = () => {
      hide(notificationId);
    };

    window.addEventListener(
      'bridge:link-request-notification-alert',
      handleLinkRequest as unknown as EventListener,
      { signal }
    );
    window.addEventListener(
      'bridge:link-request-cancel',
      handleLinkRequestCancel as EventListener,
      { signal }
    );

    return () => {
      abortController.abort();
    };
  });

  return (
    <>
      <RoomBootReadinessPublisher ready={readiness.isReady} roomId={roomId} />
      <Common roomId={roomId} />
      {readiness.showOfflinePlaceholder ? (
        <Placeholder roomId={roomId} />
      ) : readiness.showPage ? (
        children
      ) : (
        <AutoLinkLoadingScreen state={SEAMLESS_LOAD_STATE} />
      )}
    </>
  );
};

/** `RoomWrapper` component. */
export const RoomWrapper = ({
  children,
  beforeAgentProvider,
  roomId
}: {
  beforeAgentProvider?: React.ReactNode;
  children?: React.ReactNode;
  roomId: M_Room['id'];
}): ReactElement => {
  if (!roomId) {
    throw new NotFoundError();
  }

  const options = useLazyRef(() => {
    // Parse query string.
    const params = parseQuery(window.location.search);

    return {
      restore: toBoolean(params['agent:restore'], { default: true })
    };
  }).current;

  return (
    <RoomProvider options={options} roomId={roomId}>
      {beforeAgentProvider}
      <AgentProvider roomId={roomId}>{children}</AgentProvider>
    </RoomProvider>
  );
};

/** `<Room>` route. */
export const Room = (props: Props): ReactElement => {
  const params = useParams();

  const roomId = normalizeId<M_Room['id']>(params.roomId);

  const user = useUser(
    (user) => ({
      name: user.firstName + (user.lastName ? ' ' + user.lastName : ''),
      avatar: user.profilePhotoUrl,
      email: user.email,
      userId: user.id
    }),
    shallow
  );

  const [lastRoomId, setLastRoomId, lastRoomLoaded] = useLastRoomIdPref();

  useEffect(() => {
    setLastRoomId(roomId);

    if (roomId) {
      setActiveStudio(roomId);
    }

    return () => {
      clearActiveStudio();
    };
  }, [roomId]);

  const { track } = useAnalytics();

  useEffect(() => {
    // Notify the Electron shell the renderer loaded; room boot-overlay dismissal
    // is published separately after the final room UI is visible.
    ElectronAPI.loaded();
  }, []);

  const observationOutcome = useDepot((µ) => µ.boot.observation?.outcome);

  useEffect(() => {
    // Studio load is the committed overlay receipt, not room mount.
    if (observationOutcome !== 'ready') {
      return;
    }
    createBootLifecycleTracker(track).trackStudioLoadSucceeded({
      studio_id: roomId
    });
  }, [track, roomId, observationOutcome]);

  const setRoomID = useBillingLimitMonitor();

  useEffect(() => {
    if (!roomId) {
      return;
    }

    setRoomID(roomId);
  }, [roomId, setRoomID]);

  const settings = useSettings();

  const { actorKey: purchaseActorKey, isCurrentAccount } = usePlanPurchases();
  const billingReturnRole = useDepot((state) =>
    roomId === undefined ? undefined : state.roomRecords?.[roomId]?.permissions?.role
  );
  const billingReturnReady =
    lastRoomLoaded &&
    lastRoomId === roomId &&
    billingReturnRole !== undefined &&
    billingReturnRole !== Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED;
  useEffect(() => {
    const vendor = config.vendor;
    // The Account sheet has no Billing panel until this Studio's permissions
    // load. Leave its return queued meanwhile, rather than consuming it into
    // an empty sheet. The existing account-scoped queue still owns replay once
    // ready, per think-in-systems (ai.knowledge/global).
    if (
      (vendor !== 'iris' && vendor !== 'ptzoptics') ||
      purchaseActorKey === undefined ||
      !isCurrentAccount() ||
      !billingReturnReady
    )
      return;
    return subscribePlanPaymentReturns(vendor, purchaseActorKey, () => {
      // A known viewer/producer consumes the display request without opening
      // Billing. Later permission changes must not replay that stale return.
      if (
        billingReturnRole === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER ||
        billingReturnRole === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN
      )
        settings(Panel.Billing);
    });
  }, [settings, purchaseActorKey, isCurrentAccount, billingReturnReady, billingReturnRole]);

  if (!roomId) {
    throw new NotFoundError();
  }

  return (
    <>
      {/* Read the committed permission context without waiting for unrelated
          Bridge readiness. This test-only checkpoint never supplies state. */}
      {window.electron?.process?.env?.IRIS_E2E_TEST === '1' && (
        <span
          hidden
          {...DataSTag(Sel.AccountSettings, 'billing-return-context', roomId)}
          data-current-account={isCurrentAccount()}
          data-current-studio={lastRoomLoaded && lastRoomId === roomId}
          data-membership-role={billingReturnRole}
        />
      )}
      <InitialBillingGate roomId={roomId}>
        <RoomWrapper
          beforeAgentProvider={<RoomBootReadinessPublisher ready={false} roomId={roomId} />}
          roomId={roomId}
        >
          {isElectronApp() && <OnboardPermissionsModal key='permissions-modal' />}
          <Guard roomId={roomId} user={user}>
            <Inner roomId={roomId} {...props} />
          </Guard>
        </RoomWrapper>
      </InitialBillingGate>
    </>
  );
};
